import { useEffect, useRef } from 'react';

import { useStateMachineInput } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { RIVE_DEFAULTS } from '../../../../hooks/rive/constants';
import { useRiveTextUpdater } from '../../../../hooks/rive/useRiveTextUpdater';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { type Banner } from '../../../../services/ui';

import styles from './RiveHeader.module.scss';
import { useRiveBannersAnimation } from './useRiveBannersAnimation';
import { getRiveBackgroundUrl } from './utils';

interface RiveHeaderProps {
  bannerCC: Banner;
  bannerSC: Banner;
  animationOnly?: Banner | null;
  currentCoinType: string;
}

const RiveHeader: React.FC<RiveHeaderProps> = observer(({ bannerCC, bannerSC, animationOnly, currentCoinType }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const riveFileUrlAnimationOnly =
    (isMobile && animationOnly?.mobileBanner?.animationUrl) || animationOnly?.desktopBanner?.animationUrl || '';
  const currentCoinTypeBanner = currentCoinType === 'GC' ? bannerCC : bannerSC;
  const bannerBackgroundUrl = getRiveBackgroundUrl(currentCoinTypeBanner, isMobile);

  const ref = useRef<HTMLDivElement>(null);

  const {
    showRive: showRiveAnimationOnly,
    RiveComponent: RiveComponentAnimationOnly,
    rive: riveAnimationOnly,
  } = useRiveBannersAnimation({
    riveFileUrl: riveFileUrlAnimationOnly,
  });

  useEffect(() => {
    if (!ref.current || !riveAnimationOnly) return;

    const ob = new IntersectionObserver(([entry]) => {
      if (!riveAnimationOnly) return;
      if (entry.isIntersecting) {
        riveAnimationOnly.play();
      } else {
        riveAnimationOnly.pause();
      }
    });

    ob.observe(ref.current);
    return () => ob.disconnect();
  }, [ref, riveAnimationOnly]);

  const isScInput = useStateMachineInput(riveAnimationOnly ?? null, RIVE_DEFAULTS.STATE_MACHINE_NAME, 'is_sc', true);

  useEffect(() => {
    if (showRiveAnimationOnly && isScInput) {
      isScInput.value = currentCoinType === 'SC';
    }
  }, [showRiveAnimationOnly, isScInput, currentCoinType]);

  useRiveTextUpdater({
    rive: showRiveAnimationOnly ? riveAnimationOnly : null,
    isRivePlaying: showRiveAnimationOnly,
  });

  return (
    <>
      <img alt="CrownCoinsCasino header" className={styles.banner} src={bannerBackgroundUrl} />
      {showRiveAnimationOnly && (
        <div ref={ref} className={styles.riveOverlay}>
          {showRiveAnimationOnly && (
            <RiveComponentAnimationOnly className={classNames(styles.animationContainer, styles.activeAnimation)} />
          )}
        </div>
      )}
    </>
  );
});

export default RiveHeader;
